/* eslint-disable @typescript-eslint/no-explicit-any */

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  ViewChild,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { Clinic } from '@core/models';
import { Store } from '@ngrx/store';
import { MapHeight } from '@shared/enums';
import { AppState, GoogleMapsApiSelectors } from 'app/store';
import { Observable, filter } from 'rxjs';

@Component({
  selector: 'app-map',
  imports: [CommonModule, GoogleMap, GoogleMapsModule],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit, OnChanges, OnInit {
  @Input() address = '';
  @Input() clinics: Clinic[] = [];
  @Input() isClinicDetailsMap = false;
  @Input() offset = 0;

  @ViewChild(GoogleMap) map!: GoogleMap;

  public apiLoaded$: Observable<boolean>;
  public markers: any[] = [];
  public mapHeight = MapHeight.Large;
  public mapWrapperClass = 'map-wrapper';

  private apiLoaded = false;
  private bounds?: google.maps.LatLngBounds;
  private currentPositionIcon: any = {
    path: google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
    strokeColor: 'blue',
    strokeWeight: 2.5,
    scale: 5,
  };
  private searchAddressMarker = new google.maps.Marker({
    position: null,
    map: null,
    title: null,
  });

  constructor(store$: Store<AppState>) {
    this.apiLoaded$ = store$.select(
      GoogleMapsApiSelectors.selectGoogleMapsApiLoaded,
    );
  }

  // use initial search address and map
  ngAfterViewInit(): void {
    this.updateMapCenter(this.address);
  }

  ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    this.mapHeight = this.isClinicDetailsMap
      ? MapHeight.Small
      : MapHeight.Large;
    // setup map bounds based on available markers
    if (
      changes['clinics'] &&
      changes['clinics'].previousValue != changes['clinics'].currentValue
    ) {
      this.markers = this.createMarkers(this.clinics);
      this.bounds = this.createBounds(this.clinics);
      this.updateMapCenter(this.address, this.bounds);
    }
  }

  ngOnInit(): void {
    this.mapWrapperClass = this.isClinicDetailsMap
      ? 'clinic-map-wrapper'
      : 'map-wrapper';
    this.apiLoaded$
      .pipe(filter((apiLoaded) => !!apiLoaded))
      .subscribe((apiLoaded) => {
        this.apiLoaded = apiLoaded;
        this.updateMapCenter(this.address);
      });
  }

  private createBounds(clinics: Clinic[]): google.maps.LatLngBounds {
    const bound = new google.maps.LatLngBounds();
    clinics.forEach((clinic) =>
      bound.extend(new google.maps.LatLng(clinic.latitude, clinic.longitude)),
    );
    return bound;
  }

  private createMarkers(clinics: Clinic[]): google.maps.MarkerOptions[] {
    return clinics.map((clinic, index) => ({
      position: new google.maps.LatLng(clinic.latitude, clinic.longitude),
      label: (index + this.offset + 1).toString(),
      title: clinic.name,
    }));
  }

  private updateMapCenter(
    queryAddress: string,
    clinicBounds?: google.maps.LatLngBounds,
  ) {
    // setup google maps places query
    const request = {
      query: queryAddress,
      fields: ['name', 'geometry'],
    };

    if (this.apiLoaded && this.map) {
      const service = new google.maps.places.PlacesService(this.map.googleMap!);

      service.findPlaceFromQuery(
        request,
        (
          results: google.maps.places.PlaceResult[] | null,
          status: google.maps.places.PlacesServiceStatus,
        ) => {
          const location = results && results[0].geometry?.location;
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            location
          ) {
            // setup interface for google map dependent on map type
            this.map.googleMap?.setOptions({
              disableDefaultUI: this.isClinicDetailsMap,
              clickableIcons: !this.isClinicDetailsMap,
              draggable: !this.isClinicDetailsMap,
              zoom: this.isClinicDetailsMap ? 12 : null,
            });
            // update map center and zoom
            this.map.googleMap?.setCenter(location);
            if (clinicBounds) {
              const bounds = clinicBounds.extend(location);
              this.map.googleMap?.fitBounds(bounds);
            }
            // update marker indicating the search address
            this.searchAddressMarker.setOptions({
              position: location,
              map: this.map.googleMap!,
              title: queryAddress,
              // clinic-searh map requires distinct icon for current position
              icon: this.isClinicDetailsMap
                ? undefined
                : this.currentPositionIcon,
            });
          }
        },
      );
    }
  }
}
